<template>
    <div class="row">
        <div class="col-12">
            <div :class="builderMode ? 'service-builder-group' : 'border-light b-radius-20 p-4'">
                <div v-if="sectionIsVisible('identity')" class="service-section">
                    <div v-if="builderMode" class="service-section__header">
                        {{ $tl("labels.booking.services.sections.general") }}
                    </div>
                    <div class="row">
                        <div class="col-5 pt-2">
                            <label class="form-label" for="category">{{ $tl("labels.booking.services.category") }} <small>*</small></label>
                        </div>
                        <div class="col-7">
                            <select
                                name="category"
                                id="category"
                                v-model="service.general.category.value"
                                required
                                class="form-select align-middle d-inline-block">
                                <option
                                    v-for="serviceCategory in ALL_DEFAULT_SERVICE_CATEGORIES_WITH_OTHER"
                                    :key="serviceCategory.value"
                                    :value="serviceCategory.value">
                                    {{ getDefaultServiceCategoryLabel(serviceCategory.value) }}
                                </option>
                            </select>
                            <small class="form-text d-block text-muted">
                                {{ $tl("labels.booking.services.categoryHelp") }}
                            </small>
                        </div>
                    </div>
                    <div class="row mb-2" v-if="service.general.category.value === 'autre'">
                        <div class="col-5 pt-2">
                            <label class="form-label" for="category"
                                >{{ $tl("labels.booking.services.categoryName") }} ({{ $tl("labels.langs.fr") }})<small>*</small></label
                            >
                        </div>
                        <div class="col-7 pt-2">
                            <input
                                type="text"
                                name="custom_category_fr"
                                id="custom_category_fr"
                                v-model="service.general.category.nameFr"
                                required
                                class="form-control" />
                        </div>
                        <div class="col-5 pt-2">
                            <label class="form-label" for="category"
                                >{{ $tl("labels.booking.services.categoryName") }} ({{ $tl("labels.langs.en") }})<small>*</small></label
                            >
                        </div>
                        <div class="col-7 pt-2">
                            <input
                                type="text"
                                name="custom_category_en"
                                id="custom_category_en"
                                v-model="service.general.category.nameEn"
                                required
                                class="form-control" />
                        </div>
                    </div>

                    <div class="row mb-3">
                        <div class="col-5 pt-2">
                            <label class="form-label" for="name">
                                {{ isSpecial ? $tl("labels.form.name") : $tl("labels.booking.services.name", restaurant.id) }}
                                <small>*</small>
                                <feather
                                    v-tooltip="getTooltipNoticeLexicon('notices.booking.servicesConfig.serviceName', restaurant.id)"
                                    type="info"
                                    class="ms-1 pointer tooltip-infos" />
                            </label>
                        </div>
                        <div class="col-7">
                            <input
                                type="text"
                                name="name"
                                v-model="service.general.name"
                                id="name"
                                required
                                class="form-control"
                                :placeholder="getDefaultServiceNamePlaceholder(service.general.category.value)" />
                            <small class="form-text d-block text-muted">
                                {{ $tl("labels.booking.services.nameHelp") }}
                            </small>
                        </div>
                    </div>

                    <div v-if="isSpecial && !builderMode">
                        <div>
                            <div class="row mb-3">
                                <div class="col-12">
                                    <strong>
                                        {{ $tl("labels.booking.services.special.dates") }}
                                    </strong>
                                    <feather
                                        v-tooltip="getTooltipNoticeLexicon('notices.booking.servicesConfig.exceptionalOpening', restaurant.id)"
                                        type="info"
                                        class="ms-1 pointer tooltip-infos" />
                                </div>
                            </div>
                            <div class="row mb-2">
                                <div class="col-5">
                                    <label class="form-label">{{ $tl("labels.startDate") }} <small>*</small></label>
                                </div>
                                <div class="col-7 date-resa-cal">
                                    <DatePicker v-model="openDate" @selected="verifDatesBegin" />
                                </div>
                            </div>
                            <div class="row mb-2">
                                <div class="col-5">
                                    <label class="form-label">{{ $tl("labels.endDate") }} <small>*</small></label>
                                </div>
                                <div class="col-7 date-resa-cal">
                                    <DatePicker v-model="closeDate" @selected="verifDatesEnd" />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="row mb-2">
                        <div class="col-5">
                            <label class="form-label">
                                {{ $tl("labels.booking.services.activeDays") }} <small>*</small>
                                <feather
                                    v-tooltip="getTooltipNoticeLexicon('notices.booking.servicesConfig.activeDays', restaurant.id)"
                                    type="info"
                                    class="ms-1 pointer tooltip-infos" />
                            </label>
                        </div>
                        <div class="col-7">
                            <template v-if="builderMode">
                                <div class="service-day-list">
                                    <label class="form-label service-day-pill" v-for="englishDay in englishDays()" :key="englishDay">
                                        <input type="checkbox" name="days[]" v-model="service.general.openDays" :value="englishDay" />
                                        <span class="service-day-label">{{ capitalize($t(`labels.days.${englishDay}`)) }}</span>
                                    </label>
                                </div>
                            </template>
                            <template v-else>
                                <label class="form-label container-box" v-for="englishDay in englishDays()" :key="englishDay">
                                    <input type="checkbox" name="days[]" v-model="service.general.openDays" :value="englishDay" />
                                    <span class="checkmark"></span> {{ capitalize($t(`labels.days.${englishDay}`)) }}
                                </label>
                            </template>
                        </div>
                    </div>
                </div>

                <div v-if="sectionIsVisible('planning')" class="service-section">
                    <div v-if="builderMode" class="service-section__header">
                        {{ $tl("labels.booking.services.sections.planning") }}
                    </div>
                    <div class="row mb-1">
                        <div class="col-5 pt-2">
                            <label class="form-label">{{ $tl("labels.booking.services.startHour", restaurant.id) }} <small>*</small></label>
                        </div>
                        <div class="col-7">
                            <vue-timepicker format="HH:mm" :minute-interval="slotIntervalMinutes" v-model="serviceHourStart"></vue-timepicker>
                        </div>
                    </div>
                    <div class="row mb-2">
                        <div class="col-5 pt-2">
                            <label class="form-label">{{ $tl("labels.booking.services.endHour", restaurant.id) }} <small>*</small></label>
                        </div>
                        <div class="col-7">
                            <vue-timepicker format="HH:mm" :minute-interval="slotIntervalMinutes" v-model="serviceHourEnd"></vue-timepicker>
                        </div>
                    </div>
                    <div class="row mb-2">
                        <div class="col-5 pt-2">
                            <label class="form-label">{{ $tl("labels.booking.services.slotsInterval") }}</label>
                        </div>
                        <div class="col-7">
                            <select v-model.number="slotInterval" class="form-select align-middle d-inline-block w-auto">
                                <option v-for="interval in slotIntervalOptions" :key="interval" :value="interval">{{ interval }} min</option>
                            </select>
                        </div>
                    </div>
                    <div class="row mb-2" v-if="displaySlots.length > 0">
                        <div class="col-5">
                            <label class="form-label">
                                {{ $tl("labels.booking.services.slots") }} <small>*</small>
                                <feather
                                    v-tooltip="getTooltipNotice('notices.booking.servicesConfig.reservationSlots')"
                                    type="info"
                                    class="ms-1 pointer tooltip-infos" />
                            </label>
                        </div>
                        <div class="col-7">
                            <div class="d-flex flex-wrap">
                                <button
                                    v-for="slot in displaySlots"
                                    :key="slot"
                                    type="button"
                                    class="btn btn-sm me-2 mb-2 service-slot-chip"
                                    :class="isSlotEnabled(slot) ? 'btn-success' : 'btn-light text-muted'"
                                    @click="toggleSlot(slot)">
                                    {{ slot }}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="sectionIsVisible('capacity')" class="service-section">
                    <div v-if="builderMode" class="service-section__header service-section__header--stacked">
                        <span>{{ $tl("labels.booking.services.sections.reservationPeople") }}</span>
                        <small>{{ $tl("infos.booking.services.reservationPeople") }}</small>
                    </div>
                    <div class="capacity-settings-list">
                        <div class="capacity-setting-row">
                            <div class="capacity-setting-copy">
                                <label class="form-label">{{ $tl("labels.booking.services.capacity.minPeople") }} <small>*</small></label>
                            </div>
                            <div class="capacity-setting-control">
                                <span v-if="service.general.use_widget_min_pers" class="capacity-current-value">
                                    {{ formatPeopleCount(widgetDefaultMinPers)
                                    }}<span
                                        class="capacity-current-value__marker"
                                        v-tooltip="$tl('labels.booking.services.capacity.widgetDefaultMarker')"
                                        :aria-label="$tl('labels.booking.services.capacity.widgetDefaultMarker')"
                                        tabindex="0"
                                        >*</span
                                    >
                                </span>
                                <div v-else class="capacity-inline-edit">
                                    <input
                                        ref="minPersInput"
                                        id="min_pers"
                                        type="number"
                                        v-model="service.general.min_pers"
                                        :required="!service.general.use_widget_min_pers"
                                        class="form-control capacity-setting-input capacity-setting-input--personalized"
                                        min="1"
                                        step="1"
                                        :aria-label="$tl('labels.booking.services.capacity.minPeopleValue')" />
                                    <span class="capacity-setting-unit">{{ peopleUnit(service.general.min_pers) }}</span>
                                </div>
                                <button
                                    type="button"
                                    class="capacity-mode-action"
                                    :class="{ 'capacity-mode-action--icon': !service.general.use_widget_min_pers }"
                                    v-tooltip="
                                        !service.general.use_widget_min_pers ? $tl('labels.booking.services.capacity.useDefaultWidgetValue') : null
                                    "
                                    :aria-label="
                                        service.general.use_widget_min_pers
                                            ? $tl('labels.booking.services.capacity.customizeForServiceShort')
                                            : $tl('labels.booking.services.capacity.useDefaultWidgetValue')
                                    "
                                    @click="toggleMinPeopleMode">
                                    <feather :type="service.general.use_widget_min_pers ? 'edit-3' : 'rotate-ccw'" size="14" />
                                    <span v-if="service.general.use_widget_min_pers">
                                        {{ $tl("labels.booking.services.capacity.customizeForServiceShort") }}
                                    </span>
                                </button>
                            </div>
                        </div>
                        <div class="capacity-setting-row">
                            <div class="capacity-setting-copy">
                                <label class="form-label">{{ $tl("labels.booking.services.capacity.maxPeople") }} <small>*</small></label>
                            </div>
                            <div class="capacity-setting-control">
                                <span v-if="service.general.use_widget_max_pers" class="capacity-current-value">
                                    {{ formatPeopleCount(widgetDefaultMaxPers)
                                    }}<span
                                        class="capacity-current-value__marker"
                                        v-tooltip="$tl('labels.booking.services.capacity.widgetDefaultMarker')"
                                        :aria-label="$tl('labels.booking.services.capacity.widgetDefaultMarker')"
                                        tabindex="0"
                                        >*</span
                                    >
                                </span>
                                <div v-else class="capacity-inline-edit">
                                    <input
                                        ref="maxPersInput"
                                        id="max_pers"
                                        type="number"
                                        v-model="service.general.max_pers"
                                        :required="!service.general.use_widget_max_pers"
                                        class="form-control capacity-setting-input capacity-setting-input--personalized"
                                        min="1"
                                        step="1"
                                        :aria-label="$tl('labels.booking.services.capacity.maxPeopleValue')" />
                                    <span class="capacity-setting-unit">{{ peopleUnit(service.general.max_pers) }}</span>
                                </div>
                                <button
                                    type="button"
                                    class="capacity-mode-action"
                                    :class="{ 'capacity-mode-action--icon': !service.general.use_widget_max_pers }"
                                    v-tooltip="
                                        !service.general.use_widget_max_pers ? $tl('labels.booking.services.capacity.useDefaultWidgetValue') : null
                                    "
                                    :aria-label="
                                        service.general.use_widget_max_pers
                                            ? $tl('labels.booking.services.capacity.customizeForServiceShort')
                                            : $tl('labels.booking.services.capacity.useDefaultWidgetValue')
                                    "
                                    @click="toggleMaxPeopleMode">
                                    <feather :type="service.general.use_widget_max_pers ? 'edit-3' : 'rotate-ccw'" size="14" />
                                    <span v-if="service.general.use_widget_max_pers">
                                        {{ $tl("labels.booking.services.capacity.customizeForServiceShort") }}
                                    </span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="sectionIsVisible('capacity')" class="service-section">
                    <div v-if="builderMode" class="service-section__header">
                        {{ $tl("labels.booking.services.sections.capacity") }}
                    </div>
                    <div class="capacity-settings-list">
                        <div class="capacity-defaults-panel">
                            <div class="capacity-defaults-panel__copy">
                                <strong>{{ $tl("labels.booking.services.capacity.defaultValuesTitle") }}</strong>
                                <small>{{ $tl("infos.booking.services.capacityDefaultValues") }}</small>
                            </div>
                            <label class="form-label capacity-default-field" for="default_slot_max_pax">
                                <span>{{ $tl("labels.booking.services.capacity.defaultCovers") }}</span>
                                <input
                                    id="default_slot_max_pax"
                                    type="number"
                                    v-model="service.general.pax.max"
                                    class="form-control capacity-setting-input"
                                    required
                                    min="1"
                                    step="1" />
                            </label>
                            <label class="form-label capacity-default-field" for="default_slot_max_reservations">
                                <span>{{ $tl("labels.booking.services.capacity.defaultReservations") }}</span>
                                <input
                                    id="default_slot_max_reservations"
                                    type="number"
                                    v-model="service.general.defaultSlotMaxReservations"
                                    class="form-control capacity-setting-input"
                                    min="0"
                                    step="1" />
                            </label>
                        </div>
                        <div v-if="!$_.isEmpty(service.general.slots)" class="capacity-slot-panel">
                            <p class="capacity-slot-panel__hint">
                                {{ $tl("infos.booking.services.capacitySelectSlotsToCustomize") }}
                            </p>
                            <div v-if="selectedSlotsCount > 0" class="capacity-selection-toolbar" role="region" aria-live="polite">
                                <span>{{ selectedSlotsLabel }}</span>
                                <button type="button" class="capacity-selection-toolbar__button" @click="showCapacityBulkModal = true">
                                    <feather type="edit-3" size="14" />
                                    {{ $tl("labels.booking.services.capacity.customizeSelected") }}
                                </button>
                            </div>
                            <div class="capacity-slot-table-wrapper">
                                <div class="capacity-slot-table">
                                    <div class="capacity-slot-table__header">
                                        <label class="form-label capacity-slot-selection">
                                            <input
                                                type="checkbox"
                                                :checked="allVisibleSlotsSelected"
                                                :indeterminate.prop="hasPartialVisibleSlotSelection"
                                                :aria-label="$tl('labels.booking.services.capacity.selectAllSlots')"
                                                @change="toggleAllVisibleSlots($event.target.checked)" />
                                        </label>
                                        <span>{{ $tl("labels.booking.services.capacity.slot") }}</span>
                                        <span>{{ $tl("labels.booking.services.capacity.covers") }}</span>
                                        <span>{{ $tl("labels.booking.services.capacity.reservationsMax") }}</span>
                                    </div>
                                    <template v-for="slotKey in sortedSlotsKeys">
                                        <div class="capacity-slot-table__row" :key="slotKey">
                                            <label class="form-label capacity-slot-selection">
                                                <input
                                                    type="checkbox"
                                                    :checked="isSlotSelected(slotKey)"
                                                    :aria-label="
                                                        $tl('labels.booking.services.capacity.selectSlot').replace(
                                                            '{slot}',
                                                            service.general.slots[slotKey].hour_start
                                                        )
                                                    "
                                                    @change="toggleSlotSelection(slotKey, $event.target.checked)" />
                                            </label>
                                            <span class="capacity-slot-time">{{ service.general.slots[slotKey].hour_start }}</span>
                                            <div class="capacity-slot-limit">
                                                <span class="capacity-slot-value">
                                                    {{ getSlotLimitDisplayLabel(slotKey, "max_pax") }}
                                                </span>
                                                <span
                                                    v-if="isSlotLimitCustom(slotKey, 'is_max_pax_enabled', 'max_pax')"
                                                    class="capacity-status-badge is-custom">
                                                    {{ $tl("labels.booking.services.capacity.custom") }}
                                                </span>
                                                <button
                                                    v-if="isSlotLimitCustom(slotKey, 'is_max_pax_enabled', 'max_pax')"
                                                    type="button"
                                                    class="capacity-reset-button"
                                                    v-tooltip="$tl('labels.booking.services.capacity.resetToDefaultTooltip')"
                                                    :aria-label="
                                                        $tl('labels.booking.services.capacity.resetSlotCovers').replace(
                                                            '{slot}',
                                                            service.general.slots[slotKey].hour_start
                                                        )
                                                    "
                                                    @click="resetSlotLimit(slotKey, 'is_max_pax_enabled', 'max_pax')">
                                                    <feather type="rotate-ccw" size="14" />
                                                </button>
                                            </div>
                                            <div class="capacity-slot-limit">
                                                <span class="capacity-slot-value">
                                                    {{ getSlotLimitDisplayLabel(slotKey, "max_reservations") }}
                                                </span>
                                                <span
                                                    v-if="isSlotLimitCustom(slotKey, 'is_max_reservations_enabled', 'max_reservations')"
                                                    class="capacity-status-badge is-custom">
                                                    {{ $tl("labels.booking.services.capacity.custom") }}
                                                </span>
                                                <button
                                                    v-if="isSlotLimitCustom(slotKey, 'is_max_reservations_enabled', 'max_reservations')"
                                                    type="button"
                                                    class="capacity-reset-button"
                                                    v-tooltip="$tl('labels.booking.services.capacity.resetToDefaultTooltip')"
                                                    :aria-label="
                                                        $tl('labels.booking.services.capacity.resetSlotReservations').replace(
                                                            '{slot}',
                                                            service.general.slots[slotKey].hour_start
                                                        )
                                                    "
                                                    @click="resetSlotLimit(slotKey, 'is_max_reservations_enabled', 'max_reservations')">
                                                    <feather type="rotate-ccw" size="14" />
                                                </button>
                                            </div>
                                        </div>
                                    </template>
                                </div>
                            </div>
                        </div>
                        <p v-else class="capacity-empty-state">
                            {{ $tl("labels.booking.services.capacity.noSlots") }}
                        </p>
                    </div>
                    <div
                        v-if="showCapacityBulkModal && selectedSlotsCount > 0"
                        class="capacity-modal-backdrop"
                        role="presentation"
                        @click.self="closeCapacityBulkModal">
                        <section class="capacity-modal" role="dialog" aria-modal="true" :aria-labelledby="'capacity_bulk_modal_title'">
                            <header class="capacity-modal__header">
                                <div>
                                    <h5 id="capacity_bulk_modal_title">{{ $tl("labels.booking.services.capacity.bulkModalTitle") }}</h5>
                                    <p>{{ selectedSlotsModalDescription }}</p>
                                </div>
                                <button
                                    type="button"
                                    class="capacity-modal__close"
                                    :aria-label="$tl('labels.form.actions.close')"
                                    @click="closeCapacityBulkModal">
                                    <feather type="x" size="18" />
                                </button>
                            </header>
                            <div class="capacity-modal__body">
                                <label class="form-label capacity-bulk-field" for="bulk_slot_max_pax">
                                    <span>{{ $tl("labels.booking.services.capacity.defaultCovers") }}</span>
                                    <input
                                        id="bulk_slot_max_pax"
                                        type="number"
                                        v-model="slotBulkMaxPax"
                                        class="form-control capacity-setting-input"
                                        min="0"
                                        step="1"
                                        :placeholder="$tl('labels.booking.services.capacity.noChange')" />
                                </label>
                                <label class="form-label capacity-bulk-field" for="bulk_slot_max_reservations">
                                    <span>{{ $tl("labels.booking.services.capacity.defaultReservations") }}</span>
                                    <input
                                        id="bulk_slot_max_reservations"
                                        type="number"
                                        v-model="slotBulkMaxReservations"
                                        class="form-control capacity-setting-input"
                                        min="0"
                                        step="1"
                                        :placeholder="$tl('labels.booking.services.capacity.noChange')" />
                                </label>
                            </div>
                            <footer class="capacity-modal__footer">
                                <button type="button" class="btn btn-link capacity-modal__secondary" @click="resetSelectedSlotsToDefaults">
                                    {{ $tl("labels.booking.services.capacity.resetSelected") }}
                                </button>
                                <div class="capacity-modal__actions">
                                    <button type="button" class="btn btn-light" @click="closeCapacityBulkModal">
                                        {{ $tl("labels.form.actions.cancel") }}
                                    </button>
                                    <button
                                        type="button"
                                        class="capacity-modal__primary"
                                        :disabled="!canApplyBulkSlotValues"
                                        @click="applyBulkSlotValues">
                                        {{ $tl("labels.booking.services.capacity.apply") }}
                                    </button>
                                </div>
                            </footer>
                        </section>
                    </div>
                </div>

                <div v-if="sectionIsVisible('rules')" class="service-section">
                    <div v-if="builderMode" class="service-section__header">
                        {{ $tl("labels.booking.services.sections.bookingParams") }}
                    </div>
                    <div class="rules-settings-list">
                        <div class="rules-setting-row rules-setting-row--compact">
                            <div class="rules-setting-copy rules-setting-copy--inline">
                                <label class="form-label">{{ $tl("labels.booking.services.enableOnWidget") }} <small>*</small></label>
                            </div>
                            <div class="rules-setting-control">
                                <div class="d-inline-flex align-items-center lead-switch">
                                    <input type="checkbox" class="switch align-self-center is-rounded" :checked="service.general.show_on_widget" />
                                    <label class="form-label" @click="service.general.show_on_widget = !service.general.show_on_widget"></label>
                                </div>
                            </div>
                        </div>
                        <div class="rules-setting-row">
                            <div class="rules-setting-copy">
                                <label class="form-label">
                                    {{ $tl("labels.booking.services.takeResas") }} <small>*</small>
                                    <feather
                                        v-tooltip="getTooltipNoticeLexicon('notices.booking.servicesConfig.limitReservation', restaurant.id)"
                                        type="info"
                                        class="ms-1 pointer tooltip-infos" />
                                </label>
                            </div>
                            <div class="rules-setting-control rules-setting-control--stacked">
                                <div class="rules-radio-group radio">
                                    <label class="form-label container-box rules-radio-choice">
                                        <input type="radio" v-model="service.general.lock.enabled" :value="false" />
                                        <span class="checkmark"></span>
                                        {{ $tl("labels.booking.services.allowReservationsDuringService") }}
                                    </label>
                                    <label class="form-label container-box rules-radio-choice">
                                        <input
                                            type="radio"
                                            v-model="service.general.lock.enabled"
                                            :value="true"
                                            @change="ensureLockReservationMinimum" />
                                        <span class="checkmark"></span>
                                        {{ $tl("labels.booking.services.blockResas") }}
                                    </label>
                                </div>
                                <div v-if="service.general.lock.enabled" class="rules-inline-fields">
                                    <label class="form-label rules-number-field" for="lock_reservation_until">
                                        <span>{{ $tl("labels.booking.services.blockBeforeStart") }}</span>
                                        <div class="rules-number-field__control">
                                            <input
                                                type="number"
                                                class="form-control"
                                                name="lock_reservation_until"
                                                v-model.number="service.general.lock.until"
                                                :min="lockReservationMinimum"
                                                step="1"
                                                id="lock_reservation_until" />
                                            <select
                                                class="form-select align-middle d-inline-block"
                                                v-model="service.general.lock.unit"
                                                @change="ensureLockReservationMinimum">
                                                <option value="minutes">{{ $tl("labels.booking.services.minutes") }}</option>
                                                <option value="hours">{{ $tl("labels.booking.services.hours") }}</option>
                                            </select>
                                        </div>
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div class="rules-setting-row">
                            <div class="rules-setting-copy">
                                <label class="form-label">
                                    {{ $tl("labels.booking.services.resasConfirm") }} <small>*</small>
                                    <feather
                                        v-tooltip="getTooltipNotice('notices.booking.servicesConfig.reservationConfirm')"
                                        type="info"
                                        class="ms-1 pointer tooltip-infos" />
                                </label>
                            </div>
                            <div class="rules-setting-control rules-setting-control--stacked">
                                <div class="rules-radio-group radio">
                                    <label class="form-label container-box rules-radio-choice">
                                        <input type="radio" v-model="service.general.autoconfirm.enabled" :value="false" />
                                        <span class="checkmark"></span>
                                        {{ $tl("labels.manual") }}
                                    </label>
                                    <label class="form-label container-box rules-radio-choice">
                                        <input type="radio" v-model="service.general.autoconfirm.enabled" :value="true" />
                                        <span class="checkmark"></span>
                                        {{ $tl("labels.auto") }}
                                    </label>
                                </div>
                                <div v-if="service.general.autoconfirm.enabled" class="autoconfirm-fields">
                                    <label class="form-label rules-number-field">
                                        <span>{{ $tl("labels.booking.services.autoconfirmUntilPercent") }}</span>
                                        <input
                                            type="number"
                                            v-model="service.general.autoconfirm.upToPercentFull"
                                            class="form-control"
                                            min="0"
                                            max="100" />
                                    </label>
                                    <label class="form-label rules-number-field">
                                        <span>{{ $tl("labels.booking.services.autoconfirmUntilPax") }}</span>
                                        <input type="number" v-model="service.general.autoconfirm.upToPax" class="form-control" min="0" />
                                    </label>
                                    <label class="form-label rules-number-field">
                                        <span>{{ $tl("labels.booking.services.autoconfirmUntilMinutes") }}</span>
                                        <input
                                            type="number"
                                            v-model="service.general.autoconfirm.upToMinutesBeforeStart"
                                            class="form-control"
                                            min="0" />
                                    </label>
                                    <label class="form-label rules-number-field rules-number-field--wide">
                                        <span>{{ $tl("labels.booking.services.blockAutoConfirm") }}</span>
                                        <input type="number" v-model.number="service.general.autoconfirm.upToNoshow" class="form-control" min="0" />
                                        <small>{{ $tl("infos.booking.services.blockAutoConfirm") }}</small>
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div class="rules-setting-row">
                            <div class="rules-setting-copy">
                                <label class="form-label">
                                    {{ $tl("labels.booking.services.cancelationDelay") }} <small>*</small>
                                    <feather
                                        v-tooltip="getTooltipNoticeLexicon('notices.booking.servicesConfig.cancellationPeriod', restaurant.id)"
                                        type="info"
                                        class="ms-1 pointer tooltip-infos" />
                                </label>
                            </div>
                            <div class="rules-setting-control">
                                <div class="rules-inline-fields">
                                    <label class="form-label rules-number-field" for="cancellation_until">
                                        <span>{{ $tl("labels.booking.services.cancellationBeforeReservation") }}</span>
                                        <div class="rules-number-field__control rules-number-field__control--single">
                                            <input
                                                id="cancellation_until"
                                                type="number"
                                                v-model="service.general.cancellation.delay"
                                                class="form-control"
                                                min="0" />
                                            <span>{{ $tl("labels.booking.services.hours") }}</span>
                                        </div>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="sectionIsVisible('rules')" class="service-section">
                    <div v-if="builderMode" class="service-section__header">
                        {{ $tl("labels.booking.services.sections.seatingPlan") }}
                    </div>
                    <div class="row mb-2 mt-4">
                        <div class="col-5 pt-2">
                            <label class="form-label">
                                {{ $tl("labels.booking.services.enableTableRotation", restaurant_id) }}
                                <feather
                                    v-tooltip="getTooltipNoticeLexicon('notices.booking.servicesConfig.tableRotation', restaurant.id)"
                                    type="info"
                                    class="ms-1 pointer tooltip-infos" />
                            </label>
                        </div>
                        <div class="col-7 h-100 d-table pt-2">
                            <div class="d-table-cell align-middle">
                                <label class="form-label container-box">
                                    <input type="checkbox" name="enable_table_rotation" v-model="service.paxes.rotation" />
                                    <span class="checkmark"></span>
                                </label>
                            </div>
                        </div>
                    </div>
                    <div v-if="service.paxes.rotation" class="average-table-time">
                        <div class="average-table-time__title">
                            {{ $tl("labels.booking.services.averageTimeAtTable", restaurant_id) }}
                        </div>
                        <div class="average-table-time__default">
                            <label class="form-label">{{ $tl("labels.booking.services.capacity.defaultAverageDuration") }}</label>
                            <vue-timepicker format="HH:mm" :minute-interval="15" v-model="defaultAverageTimeAtTable"></vue-timepicker>
                        </div>
                        <div class="average-table-time__customize lead-switch">
                            <input
                                type="checkbox"
                                class="switch align-self-center is-rounded"
                                :checked="customizeAverageTimeByPax"
                                :aria-label="$tl('labels.booking.services.capacity.customizeByPeople')"
                                @change="toggleAverageTimeCustomization" />
                            <label class="form-label" @click="toggleAverageTimeCustomization"></label>
                            <span>{{ $tl("labels.booking.services.capacity.customizeByPeople") }}</span>
                        </div>
                        <div v-if="customizeAverageTimeByPax" class="average-table-time__table">
                            <div class="average-table-time__header">
                                <span>{{ $tl("labels.booking.services.capacity.people") }}</span>
                                <span>{{ $tl("labels.booking.services.capacity.duration") }}</span>
                            </div>
                            <template v-for="pax in averageTableDurationRows">
                                <div class="average-table-time__row" :key="pax.pax">
                                    <span>{{ formatPaxLabel(pax.pax) }}</span>
                                    <vue-timepicker v-if="pax.pax < 10" format="HH:mm" :minute-interval="15" v-model="pax.duration"></vue-timepicker>
                                    <vue-timepicker
                                        v-else
                                        format="HH:mm"
                                        @change="setPaxesHigherThanTen($event)"
                                        :minute-interval="15"
                                        v-model="pax.duration"></vue-timepicker>
                                </div>
                            </template>
                        </div>
                    </div>
                    <template v-if="hasFeatSeatingPlan">
                        <div class="row mb-2 mt-2">
                            <div class="col-5 pt-2">
                                <label class="form-label">
                                    {{ $tl("labels.booking.seatingPlan.title") }}
                                </label>
                            </div>
                            <div class="col-7 h-100 d-table pt-2">
                                <div>
                                    <select
                                        class="form-select align-middle d-inline-block"
                                        v-model="service.seatingPlan.value"
                                        :disabled="!restaurant.hasASeatingPlan"
                                        v-tooltip="
                                            !restaurant.hasASeatingPlan
                                                ? {
                                                      content: $t('infos.booking.services.enableSeatingPlan'),
                                                  }
                                                : null
                                        ">
                                        <option :value="null">--</option>
                                        <option v-for="seatingPlan in seatingPlans" :key="seatingPlan.id" :value="seatingPlan">
                                            {{ seatingPlan.name }}
                                        </option>
                                    </select>
                                </div>
                            </div>
                        </div>
                        <div class="row mb-2 mt-2" v-if="service.seatingPlan.value">
                            <div class="col-5 pt-2">
                                <label class="form-label">
                                    {{ $tl("labels.booking.services.enableAlgorithm", restaurant_id) }}
                                    <feather
                                        v-tooltip="getTooltipNoticeLexicon('notices.booking.servicesConfig.automaticSeatingPlan', restaurant.id)"
                                        type="info"
                                        class="ms-1 pointer tooltip-infos" />
                                </label>
                            </div>
                            <div class="col-7 h-100 d-table pt-2">
                                <div class="d-table-cell align-middle">
                                    <label
                                        class="form-label container-box"
                                        v-tooltip="
                                            !restaurant.hasASeatingPlan
                                                ? {
                                                      content: $t('infos.booking.services.enableSeatingPlan'),
                                                  }
                                                : null
                                        ">
                                        <input
                                            type="checkbox"
                                            v-model="service.seatingPlan.algorithm_enabled"
                                            :disabled="!restaurant.hasASeatingPlan" />
                                        <span class="checkmark"></span>
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div class="row mb-2 mt-2" v-if="service.seatingPlan.algorithm_enabled">
                            <div class="col-5 pt-2">
                                <label class="form-label">
                                    {{ $tl("labels.booking.services.allowClientsChooseRoom") }}
                                </label>
                            </div>
                            <div class="col-7 h-100 d-table pt-2">
                                <div class="d-table-cell align-middle">
                                    <label class="form-label container-box">
                                        <input type="checkbox" v-model="service.seatingPlan.allow_clients_choose_room" />
                                        <span class="checkmark"></span>
                                    </label>
                                </div>
                                <div class="radio" v-if="service.seatingPlan.allow_clients_choose_room">
                                    <div class="d-table-cell align-middle">
                                        <label class="form-label container-box">
                                            <input type="radio" v-model="service.seatingPlan.allow_clients_choose_room_mandatory" value="0" />
                                            <span class="checkmark"></span> {{ $tl("labels.optional") }}
                                        </label>
                                        <label class="form-label container-box">
                                            <input type="radio" v-model="service.seatingPlan.allow_clients_choose_room_mandatory" value="1" />
                                            <span class="checkmark"></span> {{ $tl("labels.mandatory") }}
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>
                </div>

                <div v-if="sectionIsVisible('externalMenu')" class="service-section restaurant-card-section">
                    <div v-if="builderMode" class="service-section__header">
                        {{ $tl("labels.booking.services.menusAndOptions.restaurantCard", restaurant.id) }}
                    </div>
                    <div class="restaurant-card-section__content">
                        <div class="restaurant-card-section__label">
                            <label class="form-label mb-0">
                                {{ $tl("labels.booking.menus.externalMenu.externalMenu", restaurant.id) }}
                                <feather
                                    v-tooltip="getTooltipNoticeLexicon('notices.booking.externalMenus', restaurant.id)"
                                    type="info"
                                    class="ms-1 pointer tooltip-infos" />
                            </label>
                        </div>
                        <div class="restaurant-card-section__options">
                            <div class="restaurant-card-section__choices radio">
                                <label class="form-label restaurant-card-choice" :class="{ 'is-selected': externalMenu.type === null }">
                                    <input type="radio" v-model="externalMenu.type" :value="null" />
                                    {{ $tl("labels.booking.menus.externalMenu.disabled") }}
                                </label>
                                <label
                                    class="form-label restaurant-card-choice"
                                    :class="{ 'is-selected': externalMenu.type === EXTERNAL_MENU_FILE.value }">
                                    <input type="radio" v-model="externalMenu.type" :value="EXTERNAL_MENU_FILE.value" />
                                    {{ $tl("labels.booking.menus.externalMenu.file.pdf") }}
                                </label>
                                <label
                                    class="form-label restaurant-card-choice"
                                    :class="{ 'is-selected': externalMenu.type === EXTERNAL_MENU_LINK.value }">
                                    <input type="radio" v-model="externalMenu.type" :value="EXTERNAL_MENU_LINK.value" />
                                    {{ $tl("labels.booking.menus.externalMenu.link.link") }}
                                </label>
                            </div>
                            <div v-if="externalMenu.type === EXTERNAL_MENU_FILE.value" class="restaurant-card-section__field">
                                <FileUploaderComponent
                                    :max-size="20"
                                    :hasDirectUpload="false"
                                    suffixId="ext-desc"
                                    :props-file="externalMenu.file"
                                    @upload-file="externalMenu.file = $event" />
                            </div>
                            <div v-else-if="externalMenu.type === EXTERNAL_MENU_LINK.value" class="restaurant-card-section__field">
                                <InputComponent
                                    v-model="externalMenu.content.link"
                                    label=""
                                    :placeholder="$t('labels.booking.menus.externalMenu.link.placeholder')"
                                    labelWrapperClasses="d-none"
                                    type="url" />
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="builderMode && showSpecialToggle && sectionIsVisible('identity')" class="service-section">
                    <div class="service-section__header">
                        {{ $tl("labels.booking.services.sections.exceptionalParams") }}
                    </div>
                    <div class="row mb-2">
                        <div class="col-5">
                            <label class="form-label">{{ $tl("labels.booking.services.special.enable") }}</label>
                        </div>
                        <div class="col-7">
                            <div class="d-inline-flex align-items-center">
                                <input type="checkbox" class="switch align-self-center is-rounded" :checked="isSpecial" />
                                <label class="form-label" @click="toggleSpecial"></label>
                            </div>
                        </div>
                    </div>
                    <small class="text-muted d-block mb-2">{{ $tl("labels.booking.services.special.description") }}</small>
                    <div v-if="isSpecial">
                        <div class="row mb-2">
                            <div class="col-5">
                                <label class="form-label">{{ $tl("labels.startDate") }} <small>*</small></label>
                            </div>
                            <div class="col-7 date-resa-cal">
                                <DatePicker v-model="openDate" @selected="verifDatesBegin" />
                            </div>
                        </div>
                        <div class="row mb-2">
                            <div class="col-5">
                                <label class="form-label">{{ $tl("labels.endDate") }} <small>*</small></label>
                            </div>
                            <div class="col-7 date-resa-cal">
                                <DatePicker v-model="closeDate" @selected="verifDatesEnd" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import VueTimepicker from "vue2-timepicker";
import OverlayLayout from "../../layouts/OverlayLayout";
import { DateTime } from "luxon";
import DefaultServiceCategoriesEnum from "../../../mixins/enums/booking/DefaultServiceCategoriesEnum";
import DatePicker from "../../../components/forms/DatePicker.vue";
import ExternalMenuTypeEnum from "../../../mixins/enums/booking/ExternalMenuTypeEnum.js";
import FileUploaderComponent from "../../forms/FileUploaderComponent.vue";
import InputComponent from "../../forms/InputComponent.vue";
import LexiconEnum from "../../../mixins/enums/LexiconEnum.js";
import normalizeBooleanFlag from "../../../utils/normalizeBooleanFlag.mjs";

export default {
    name: "GeneralGroup",
    components: {
        VueTimepicker,
        OverlayLayout,
        DatePicker,
        FileUploaderComponent,
        InputComponent,
    },
    data() {
        return {
            canEmitUpdate: false,
            externalMenu: {
                type: null,
                content: {
                    link: "",
                    file: "",
                },
                hasFile: false,
                file: null,
            },
            slotInterval: 15,
            slotIntervalOptions: [10, 15, 20, 30, 45, 60],
            lastGeneratedSlots: [],
            slotBulkMaxPax: null,
            slotBulkMaxReservations: null,
            selectedSlotKeys: [],
            showCapacityBulkModal: false,
            customizeAverageTimeByPax: false,
            defaultAverageTimeAtTableValue: { HH: "03", mm: "00" },
        };
    },
    mixins: [DefaultServiceCategoriesEnum, ExternalMenuTypeEnum, LexiconEnum],
    props: {
        seatingPlans: {
            default: function () {
                return [];
            },
        },
        isSpecial: {
            default: false,
        },
        service: {
            required: true,
        },
        widget: {
            required: true,
        },
        restaurant: {
            required: true,
        },
        externalMenuFromProps: {
            type: Object,
            required: true,
        },
        builderMode: {
            type: Boolean,
            default: false,
        },
        showSpecialToggle: {
            type: Boolean,
            default: false,
        },
        visibleSections: {
            type: Array,
            default: null,
        },
        isSaving: {
            type: Boolean,
            default: false,
        },
    },
    created() {
        if (this.isSpecial && typeof this.service.general.special !== "undefined") {
            if (!this.service.general.special.openDate) {
                this.service.general.special.openDate = this.getDateTime();
            }
            if (!this.service.general.special.openDate) {
                this.service.general.special.openDate = this.getDateTime();
            }
        }
    },
    mounted() {
        this.initExternalMenu();
        this.initializeSlots();
        this.defaultAverageTimeAtTableValue = this.cloneDuration((this.service.paxes.value || [])[0]?.duration);
        this.customizeAverageTimeByPax = this.hasCustomAverageTimeByPax();
        this.canEmitUpdate = true;
    },
    computed: {
        restaurantFromStore() {
            return this.$store.getters["restaurants/findRestaurantById"](this.restaurant.id);
        },
        hasFeatSeatingPlan() {
            return typeof this.restaurantFromStore !== "undefined" && this.restaurantFromStore.feat_seating_plan;
        },
        slotIntervalMinutes() {
            const interval = Number.parseInt(this.slotInterval, 10);
            if (isNaN(interval) || interval <= 0) {
                return 15;
            }

            return interval;
        },
        serviceStartMinutes() {
            return this.timeToMinutes(this.service.general.openHours.start);
        },
        serviceEndMinutes() {
            return this.timeToMinutes(this.service.general.openHours.end);
        },
        hasValidServiceHours() {
            return this.isHourSet(this.serviceHourStart) && this.isHourSet(this.serviceHourEnd);
        },
        slotSortAnchor() {
            if (!this.hasValidServiceHours || this.serviceStartMinutes === null || this.serviceEndMinutes === null) {
                return null;
            }

            if (this.serviceEndMinutes < this.serviceStartMinutes) {
                return this.serviceStartMinutes;
            }

            return null;
        },
        openDate: {
            get() {
                if (this.isSpecial && typeof this.service.general.special !== "undefined") {
                    return this.service.general.special.openDate;
                }
                return undefined;
            },
            set(date) {
                this.service.general.special.openDate = this.getDateTime(date, false);
            },
        },
        closeDate: {
            get() {
                if (this.isSpecial && typeof this.service.general.special !== "undefined") {
                    return this.service.general.special.closeDate;
                }
                return undefined;
            },
            set(date) {
                this.service.general.special.closeDate = this.getDateTime(date, false);
            },
        },
        sortedSlotsKeys() {
            return this.sortSlotsByTime(Object.keys(this.service.general.slots || {}), this.slotSortAnchor);
        },
        visibleSlotKeys() {
            return this.sortedSlotsKeys;
        },
        selectedVisibleSlotKeys() {
            return this.selectedSlotKeys.filter((slotKey) => this.visibleSlotKeys.includes(slotKey));
        },
        selectedSlotsCount() {
            return this.selectedVisibleSlotKeys.length;
        },
        selectedSlotsLabel() {
            const key = this.selectedSlotsCount > 1 ? "selectedSlotsPlural" : "selectedSlotsSingular";

            return this.$tl(`labels.booking.services.capacity.${key}`).replace("{count}", this.selectedSlotsCount);
        },
        selectedSlotsModalDescription() {
            const slots = this.selectedVisibleSlotKeys.map((slotKey) => this.service.general.slots[slotKey]?.hour_start || slotKey).join(", ");

            return this.$tl("labels.booking.services.capacity.bulkModalDescription").replace("{slots}", slots);
        },
        allVisibleSlotsSelected() {
            return this.visibleSlotKeys.length > 0 && this.selectedSlotsCount === this.visibleSlotKeys.length;
        },
        hasPartialVisibleSlotSelection() {
            return this.selectedSlotsCount > 0 && !this.allVisibleSlotsSelected;
        },
        canApplyBulkSlotValues() {
            if (this.selectedSlotsCount === 0) {
                return false;
            }

            const hasPaxValue = this.hasSlotLimitInputValue(this.slotBulkMaxPax);
            const hasReservationsValue = this.hasSlotLimitInputValue(this.slotBulkMaxReservations);

            if (!hasPaxValue && !hasReservationsValue) {
                return false;
            }

            return (
                (!hasPaxValue || this.isValidSlotLimitValue(this.slotBulkMaxPax)) &&
                (!hasReservationsValue || this.isValidSlotLimitValue(this.slotBulkMaxReservations))
            );
        },
        serviceHourStart: {
            get() {
                if (this.service.general.openHours.start) {
                    return {
                        HH: this.service.general.openHours.start.split(":")[0],
                        mm: this.service.general.openHours.start.split(":")[1],
                    };
                }

                return { HH: "00", mm: "00" };
            },
            set(value) {
                if (
                    value.HH &&
                    value.HH != undefined &&
                    value.HH != null &&
                    value.HH !== "HH" &&
                    value.mm &&
                    value.mm != undefined &&
                    value.mm != null &&
                    value.mm !== "mm"
                ) {
                    const hourValue = Number.parseInt(value.HH);
                    const minValue = Number.parseInt(value.mm);

                    if (!isNaN(hourValue) && !isNaN(minValue)) {
                        this.service.general.openHours.start = `${value.HH}:${value.mm}`;
                        this.syncSlotsFromAvailable();
                    }
                }
            },
        },
        serviceHourEnd: {
            get() {
                if (this.service.general.openHours.end) {
                    return {
                        HH: this.service.general.openHours.end.split(":")[0],
                        mm: this.service.general.openHours.end.split(":")[1],
                    };
                }

                return { HH: "00", mm: "00" };
            },
            set(value) {
                if (
                    value.HH &&
                    value.HH != undefined &&
                    value.HH != null &&
                    value.HH !== "HH" &&
                    value.mm &&
                    value.mm != undefined &&
                    value.mm != null &&
                    value.mm !== "mm"
                ) {
                    const hourValue = Number.parseInt(value.HH);
                    const minValue = Number.parseInt(value.mm);

                    if (!isNaN(hourValue) && !isNaN(minValue)) {
                        this.service.general.openHours.end = `${value.HH}:${value.mm}`;
                        this.syncSlotsFromAvailable();
                    }
                }
            },
        },
        availableSlots() {
            if (!this.hasValidServiceHours || this.serviceStartMinutes === null || this.serviceEndMinutes === null) {
                return [];
            }

            return this.buildSlotsBetween(this.serviceStartMinutes, this.serviceEndMinutes, this.slotIntervalMinutes);
        },
        displaySlots() {
            const generatedSlots = this.availableSlots;
            const customSlots = Object.keys(this.service.general.slots || {}).filter((slot) => !generatedSlots.includes(slot));
            const mergedSlots = [...generatedSlots, ...customSlots];

            return this.sortSlotsByTime(mergedSlots, this.slotSortAnchor);
        },
        averageTableDurationRows() {
            return (this.service.paxes.value || []).filter((pax) => pax.pax <= 10);
        },
        defaultAverageTimeAtTable: {
            get() {
                return this.defaultAverageTimeAtTableValue;
            },
            set(value) {
                this.defaultAverageTimeAtTableValue = this.cloneDuration(value);

                if (!this.customizeAverageTimeByPax) {
                    this.applyDefaultAverageTimeAtTable(value);
                }
            },
        },
        widgetDefaultMinPers() {
            return this.widget?.min_pax ?? null;
        },
        widgetDefaultMaxPers() {
            if (!this.widget) {
                return null;
            }

            if (this.widget.enable_children && this.widget.max_children && this.widget.enable_children === 1) {
                return Number(this.widget.max_pax || 0) + Number(this.widget.max_children || 0);
            }

            return this.widget.max_pax ?? null;
        },
        lockReservationMinimum() {
            return this.service.general.lock.unit === "hours" ? 1 : 5;
        },
        maxPax() {
            if (this.widget && this.widget.max_pax) {
                if (this.widget.enable_children && this.widget.max_children && this.widget.enable_children === 1) {
                    return this.widget.max_pax + this.widget.max_children;
                }

                return this.widget.max_pax;
            }
        },
        restaurant_id() {
            return this.$route.params.restaurant_id;
        },
    },
    watch: {
        slotInterval() {
            this.syncSlotsFromAvailable({ pruneToAvailable: true });
        },
        "service.paxes.rotation": {
            handler: function (newVal) {
                this.service.paxes.value = [];

                if (newVal === true) {
                    var defaultDuration = {
                        HH: "01",
                        mm: "00",
                    };

                    if (this.isHourSet(this.serviceHourStart) && this.isHourSet(this.serviceHourEnd)) {
                        const nowDateTime = this.getDateTime().startOf("day");
                        const hourStart = nowDateTime.plus({
                            hours: this.serviceHourStart.HH,
                            minutes: this.serviceHourStart.mm,
                        });
                        const hourEnd = nowDateTime.plus({
                            hours: this.serviceHourEnd.HH,
                            minutes: this.serviceHourEnd.mm,
                        });

                        if (hourEnd > hourStart) {
                            const { hours, minutes } = hourEnd.diff(hourStart, ["hours", "minutes"]).toObject();

                            if (hours) {
                                defaultDuration.HH = hours < 10 ? `0${hours}` : hours.toString();
                            } else {
                                defaultDuration.HH = "00";
                            }

                            if (minutes) {
                                defaultDuration.mm = minutes < 10 ? `0${minutes}` : minutes.toString();
                            } else {
                                defaultDuration.mm = "00";
                            }
                        }
                    }

                    this.$_.range(1, this.maxPax > 10 ? this.maxPax + 1 : 11).forEach((pax) => {
                        this.service.paxes.value.push({
                            duration: Object.assign({}, defaultDuration),
                            pax,
                        });
                    });

                    this.defaultAverageTimeAtTableValue = Object.assign({}, defaultDuration);
                    this.customizeAverageTimeByPax = false;
                }
            },
        },
        "service.seatingPlan.value": {
            handler(newVal) {
                if (!newVal) {
                    this.service.seatingPlan.algorithm_enabled = false;
                    this.service.seatingPlan.allow_clients_choose_room = false;
                    this.service.seatingPlan.allow_clients_choose_room_mandatory = 0;
                }
            },
        },
        externalMenu: {
            handler(val) {
                this.emitEvent("update-external-menus", val);
            },
            deep: true,
        },
    },
    methods: {
        sectionIsVisible(section) {
            if (!this.builderMode) {
                return true;
            }

            return Array.isArray(this.visibleSections) && this.visibleSections.includes(section);
        },
        peopleUnit(value) {
            return Number(value) === 1
                ? this.$tl("labels.booking.services.capacity.person")
                : this.$tl("labels.booking.services.capacity.peopleLower");
        },
        formatPeopleCount(value) {
            if (value === null || typeof value === "undefined" || value === "") {
                return "-";
            }

            return `${value} ${this.peopleUnit(value)}`;
        },
        focusCapacityInput(refName) {
            this.$nextTick(() => {
                const input = this.$refs[refName];
                if (input && typeof input.focus === "function") {
                    input.focus();
                    input.select();
                }
            });
        },
        toggleMinPeopleMode() {
            if (this.service.general.use_widget_min_pers) {
                if (this.widgetDefaultMinPers !== null && typeof this.widgetDefaultMinPers !== "undefined" && this.widgetDefaultMinPers !== "") {
                    this.$set(this.service.general, "min_pers", this.widgetDefaultMinPers);
                }

                this.service.general.use_widget_min_pers = false;
                this.focusCapacityInput("minPersInput");
                return;
            }

            this.service.general.use_widget_min_pers = true;
        },
        toggleMaxPeopleMode() {
            if (this.service.general.use_widget_max_pers) {
                if (this.widgetDefaultMaxPers !== null && typeof this.widgetDefaultMaxPers !== "undefined" && this.widgetDefaultMaxPers !== "") {
                    this.$set(this.service.general, "max_pers", this.widgetDefaultMaxPers);
                }

                this.service.general.use_widget_max_pers = false;
                this.focusCapacityInput("maxPersInput");
                return;
            }

            this.service.general.use_widget_max_pers = true;
        },
        emitEvent(eventName, val) {
            if (!this.canEmitUpdate) {
                return;
            }
            this.$emit(eventName, val);
        },
        toggleSpecial() {
            this.$emit("toggle-special", !this.isSpecial);
        },
        initExternalMenu() {
            if (typeof this.service.general.defaultSlotMaxReservations === "undefined") {
                this.$set(this.service.general, "defaultSlotMaxReservations", null);
            }
            if (typeof this.service.general.lock.unit === "undefined") {
                this.$set(this.service.general.lock, "unit", "minutes");
            }
            if (typeof this.service.general.autoconfirm.enabled !== "boolean") {
                this.$set(this.service.general.autoconfirm, "enabled", normalizeBooleanFlag(this.service.general.autoconfirm.enabled));
            }
            this.ensureLockReservationMinimum();
            this.externalMenu = this.externalMenuFromProps;
        },
        ensureLockReservationMinimum() {
            if (!this.service.general.lock.enabled) {
                return;
            }

            const minimum = this.lockReservationMinimum;
            const currentValue = Number(this.service.general.lock.until);
            if (!Number.isFinite(currentValue) || currentValue < minimum) {
                this.$set(this.service.general.lock, "until", minimum);
            }
        },
        buildSlotsBetween(startMinutes, endMinutes, intervalMinutes) {
            if (startMinutes === null || endMinutes === null || intervalMinutes <= 0) {
                return [];
            }

            let end = endMinutes;
            if (end < startMinutes) {
                end += 24 * 60;
            }

            const slots = [];
            for (let current = startMinutes; current <= end; current += intervalMinutes) {
                slots.push(this.formatMinutesToTime(current));
            }

            return slots;
        },
        formatMinutesToTime(minutes) {
            const normalized = ((minutes % (24 * 60)) + 24 * 60) % (24 * 60);
            const hours = Math.floor(normalized / 60);
            const mins = normalized % 60;

            return `${hours}`.padStart(2, "0") + ":" + `${mins}`.padStart(2, "0");
        },
        sortSlotsByTime(slots, wrapFromMinutes = null) {
            return [...slots].sort((slotA, slotB) => {
                const minutesA = this.timeToMinutes(slotA);
                const minutesB = this.timeToMinutes(slotB);

                if (minutesA === null || minutesB === null) {
                    return `${slotA}`.localeCompare(`${slotB}`);
                }

                const adjustedA = wrapFromMinutes !== null && minutesA < wrapFromMinutes ? minutesA + 24 * 60 : minutesA;
                const adjustedB = wrapFromMinutes !== null && minutesB < wrapFromMinutes ? minutesB + 24 * 60 : minutesB;

                return adjustedA - adjustedB;
            });
        },
        initializeSlots() {
            if (!this.hasValidServiceHours) {
                return;
            }

            const slotKeys = Object.keys(this.service.general.slots || {});
            if (slotKeys.length === 0 && this.service.general.openHours.start === "00:00" && this.service.general.openHours.end === "00:00") {
                return;
            }
            if (slotKeys.length === 0) {
                this.syncSlotsFromAvailable({ forceAuto: true });
                return;
            }

            if (this.areSlotListsEqual(slotKeys, this.availableSlots)) {
                this.lastGeneratedSlots = [...this.availableSlots];
            }
        },
        verifDatesBegin(date) {
            if (this.getDateTime(date, false) > this.closeDate) {
                this.closeDate = date;
            }
        },
        verifDatesEnd(date) {
            if (this.getDateTime(date, false) < this.beginDate) {
                this.beginDate = date;
            }
        },
        areSlotListsEqual(listA, listB) {
            if (listA.length !== listB.length) {
                return false;
            }
            const sortedA = [...listA].sort();
            const sortedB = [...listB].sort();

            return sortedA.every((value, index) => value === sortedB[index]);
        },
        isSlotsAutoManaged() {
            if (this.lastGeneratedSlots.length === 0) {
                return false;
            }

            return this.areSlotListsEqual(Object.keys(this.service.general.slots || {}), this.lastGeneratedSlots);
        },
        syncSlotsFromAvailable({ forceAuto = false, pruneToAvailable = false } = {}) {
            if (!this.hasValidServiceHours) {
                return;
            }

            const generatedSlots = this.availableSlots;
            const currentSlots = this.service.general.slots || {};
            const shouldForceAuto = forceAuto || Object.keys(currentSlots).length === 0;
            const autoManaged = shouldForceAuto || this.isSlotsAutoManaged();
            let nextSlots = {};

            if (autoManaged) {
                generatedSlots.forEach((slot) => {
                    nextSlots[slot] = currentSlots[slot] || this.buildSlotEntry(slot);
                });
                this.lastGeneratedSlots = [...generatedSlots];
            } else {
                const allowedSlots = pruneToAvailable ? new Set(generatedSlots) : null;
                Object.keys(currentSlots).forEach((slot) => {
                    if (this.isSlotWithinRange(slot)) {
                        if (allowedSlots && !allowedSlots.has(slot)) {
                            return;
                        }
                        nextSlots[slot] = currentSlots[slot];
                    }
                });

                if (!pruneToAvailable && Object.keys(nextSlots).length === 0 && generatedSlots.length > 0) {
                    generatedSlots.forEach((slot) => {
                        nextSlots[slot] = this.buildSlotEntry(slot);
                    });
                    this.lastGeneratedSlots = [...generatedSlots];
                }
            }

            this.$set(this.service.general, "slots", nextSlots);
        },
        buildSlotEntry(slot) {
            return {
                hour_start: slot,
                is_max_pax_enabled: false,
                max_pax: null,
                is_max_reservations_enabled: false,
                max_reservations: null,
            };
        },
        isSlotEnabled(slot) {
            return Object.prototype.hasOwnProperty.call(this.service.general.slots || {}, slot);
        },
        toggleSlot(slot) {
            if (this.isSlotEnabled(slot)) {
                this.$set(this.service.general, "slots", this.$_.omit(this.service.general.slots, slot));
                this.toggleSlotSelection(slot, false);
                return;
            }

            this.$set(this.service.general.slots, slot, this.buildSlotEntry(slot));
        },
        timeToMinutes(value) {
            if (!value || typeof value !== "string" || !value.includes(":")) {
                return null;
            }

            const [hours, minutes] = value.split(":").map((part) => Number.parseInt(part, 10));
            if (isNaN(hours) || isNaN(minutes)) {
                return null;
            }

            return hours * 60 + minutes;
        },
        isSlotWithinRange(slot) {
            if (!this.hasValidServiceHours) {
                return true;
            }

            const slotMinutes = this.timeToMinutes(slot);
            const startMinutes = this.serviceStartMinutes;
            const endMinutes = this.serviceEndMinutes;

            if (slotMinutes === null || startMinutes === null || endMinutes === null) {
                return true;
            }

            if (endMinutes < startMinutes) {
                return slotMinutes >= startMinutes || slotMinutes <= endMinutes;
            }

            return slotMinutes >= startMinutes && slotMinutes <= endMinutes;
        },
        hasSlotLimitInputValue(value) {
            return value !== "" && value !== null && typeof value !== "undefined";
        },
        isValidSlotLimitValue(value) {
            if (!this.hasSlotLimitInputValue(value)) {
                return false;
            }

            const numericValue = Number(value);

            return Number.isInteger(numericValue) && numericValue >= 0;
        },
        getDefaultSlotLimitValue(valueKey) {
            if (valueKey === "max_pax") {
                return this.service.general.pax.max ?? "";
            }

            return this.service.general.defaultSlotMaxReservations ?? "";
        },
        isSlotLimitCustom(slotKey, enabledKey, valueKey) {
            const slot = this.service.general.slots[slotKey];

            return Boolean(slot && slot[enabledKey]) && slot[valueKey] !== null && typeof slot[valueKey] !== "undefined";
        },
        getSlotLimitDisplayValue(slotKey, valueKey) {
            const slot = this.service.general.slots[slotKey];
            if (!slot) {
                return "";
            }

            const enabledKey = valueKey === "max_pax" ? "is_max_pax_enabled" : "is_max_reservations_enabled";
            if (this.isSlotLimitCustom(slotKey, enabledKey, valueKey)) {
                return slot[valueKey];
            }

            return this.getDefaultSlotLimitValue(valueKey);
        },
        getSlotLimitDisplayLabel(slotKey, valueKey) {
            const value = this.getSlotLimitDisplayValue(slotKey, valueKey);

            if (!this.hasSlotLimitInputValue(value)) {
                return this.$tl("labels.booking.services.capacity.emptyDefault");
            }

            return value;
        },
        setSlotCustomLimit(slotKey, enabledKey, valueKey, value) {
            if (!this.service.general.slots[slotKey]) {
                return;
            }

            this.$set(this.service.general.slots[slotKey], enabledKey, 1);
            this.$set(this.service.general.slots[slotKey], valueKey, value);
        },
        resetSlotLimit(slotKey, enabledKey, valueKey) {
            if (!this.service.general.slots[slotKey]) {
                return;
            }

            this.$set(this.service.general.slots[slotKey], enabledKey, 0);
            this.$set(this.service.general.slots[slotKey], valueKey, null);
        },
        isSlotSelected(slotKey) {
            return this.selectedSlotKeys.includes(slotKey);
        },
        toggleSlotSelection(slotKey, selected) {
            if (selected && !this.isSlotSelected(slotKey)) {
                this.selectedSlotKeys = [...this.selectedSlotKeys, slotKey];
                return;
            }

            if (!selected) {
                this.selectedSlotKeys = this.selectedSlotKeys.filter((selectedSlotKey) => selectedSlotKey !== slotKey);
                if (this.selectedVisibleSlotKeys.length === 0) {
                    this.closeCapacityBulkModal();
                }
            }
        },
        toggleAllVisibleSlots(selected) {
            if (selected) {
                this.selectedSlotKeys = [...this.visibleSlotKeys];
                return;
            }

            this.selectedSlotKeys = this.selectedSlotKeys.filter((slotKey) => !this.visibleSlotKeys.includes(slotKey));
            this.closeCapacityBulkModal();
        },
        closeCapacityBulkModal() {
            this.showCapacityBulkModal = false;
        },
        countSelectedCustomSlotOverrides(targets) {
            const affectedSlots = new Set();

            this.selectedVisibleSlotKeys.forEach((slotKey) => {
                targets.forEach(({ enabledKey, valueKey }) => {
                    if (this.isSlotLimitCustom(slotKey, enabledKey, valueKey)) {
                        affectedSlots.add(slotKey);
                    }
                });
            });

            return affectedSlots.size;
        },
        confirmBulkSlotOverwrite(targets) {
            const customSlotsCount = this.countSelectedCustomSlotOverrides(targets);
            if (customSlotsCount === 0) {
                return true;
            }

            return confirm(this.$tl("questions.booking.services.capacityOverwrite").replace("{count}", customSlotsCount));
        },
        notifyCapacityActionSuccess() {
            if (typeof this.$notify !== "function") {
                return;
            }

            this.$notify({
                group: "notification",
                type: "success",
                title: this.$tl("success.booking.services.capacityUpdated"),
            });
        },
        applyBulkSlotValues() {
            if (!this.canApplyBulkSlotValues) {
                return;
            }

            const targets = [];
            if (this.hasSlotLimitInputValue(this.slotBulkMaxPax)) {
                targets.push({ enabledKey: "is_max_pax_enabled", valueKey: "max_pax", value: this.slotBulkMaxPax });
            }
            if (this.hasSlotLimitInputValue(this.slotBulkMaxReservations)) {
                targets.push({
                    enabledKey: "is_max_reservations_enabled",
                    valueKey: "max_reservations",
                    value: this.slotBulkMaxReservations,
                });
            }

            if (!this.confirmBulkSlotOverwrite(targets)) {
                return;
            }

            this.selectedVisibleSlotKeys.forEach((slotKey) => {
                targets.forEach(({ enabledKey, valueKey, value }) => {
                    this.setSlotCustomLimit(slotKey, enabledKey, valueKey, value);
                });
            });

            this.slotBulkMaxPax = null;
            this.slotBulkMaxReservations = null;
            this.selectedSlotKeys = [];
            this.closeCapacityBulkModal();
            this.notifyCapacityActionSuccess();
        },
        resetSelectedSlotsToDefaults() {
            if (this.selectedSlotsCount === 0) {
                return;
            }

            this.selectedVisibleSlotKeys.forEach((slotKey) => {
                this.resetSlotLimit(slotKey, "is_max_pax_enabled", "max_pax");
                this.resetSlotLimit(slotKey, "is_max_reservations_enabled", "max_reservations");
            });

            this.selectedSlotKeys = [];
            this.closeCapacityBulkModal();
            this.notifyCapacityActionSuccess();
        },
        setPaxesHigherThanTen(event) {
            for (const index in this.service.paxes.value) {
                const pax = this.service.paxes.value[index];
                if (pax.pax > 10) {
                    this.service.paxes.value[index].duration = event.data;
                }
            }
        },
        durationToString(duration) {
            if (!duration) {
                return "";
            }

            return `${duration.HH || "00"}:${duration.mm || "00"}`;
        },
        cloneDuration(duration) {
            return Object.assign({}, duration || { HH: "03", mm: "00" });
        },
        formatPaxLabel(pax) {
            const paxLabel = pax === 10 ? "+10" : pax;
            return `${paxLabel} ${
                pax === 1 ? this.$tl("labels.booking.services.capacity.person") : this.$tl("labels.booking.services.capacity.peopleLower")
            }`;
        },
        hasCustomAverageTimeByPax() {
            const paxes = this.service.paxes.value || [];
            if (paxes.length <= 1) {
                return false;
            }

            const defaultDuration = this.durationToString(paxes[0].duration);

            return paxes.some((pax) => this.durationToString(pax.duration) !== defaultDuration);
        },
        applyDefaultAverageTimeAtTable(duration) {
            const nextDuration = this.cloneDuration(duration);

            (this.service.paxes.value || []).forEach((pax, index) => {
                this.$set(this.service.paxes.value[index], "duration", this.cloneDuration(nextDuration));
            });
        },
        toggleAverageTimeCustomization() {
            const nextValue = !this.customizeAverageTimeByPax;
            this.customizeAverageTimeByPax = nextValue;

            if (!nextValue) {
                this.applyDefaultAverageTimeAtTable(this.defaultAverageTimeAtTable);
            }
        },
        isHourSet(obj) {
            return (
                obj && obj.HH && obj.HH !== "HH" && obj.mm && obj.mm !== "mm" && !isNaN(Number.parseInt(obj.HH)) && !isNaN(Number.parseInt(obj.mm))
            );
        },
        clearErrors() {
            this.$emit("clear-errors");
        },
        displayError(error) {
            this.$emit("has-errors", error);
        },
        loadingStart() {
            this.$emit("loading-start");
        },
        loadingStop() {
            this.$emit("loading-stop");
        },
    },
};
</script>

<style scoped>
.service-section__header--stacked {
    display: grid;
    gap: 4px;
}

.service-section__header--stacked small {
    color: #7a869a;
    font-size: 12px;
    font-weight: 600;
    line-height: 1.4;
    text-transform: none;
}

.capacity-settings-list {
    display: grid;
    gap: 12px;
}

.capacity-setting-row {
    display: grid;
    grid-template-columns: minmax(220px, 0.48fr) minmax(0, 1fr);
    gap: 18px;
    align-items: start;
    padding: 12px 0;
    border-bottom: 1px solid #edf2f8;
}

.capacity-setting-row:last-child {
    border-bottom: 0;
}

.capacity-setting-copy label {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-bottom: 0;
    color: #344055;
    font-weight: 700;
    line-height: 1.45;
}

.capacity-setting-copy small {
    display: block;
    margin-top: 5px;
    line-height: 1.45;
}

.capacity-setting-control {
    display: grid;
    grid-template-columns: minmax(180px, max-content) max-content;
    align-items: center;
    gap: 10px 14px;
    min-width: 0;
}

.capacity-current-value {
    display: inline-flex;
    align-items: center;
    min-height: 34px;
    color: #4b5871;
    font-size: 12px;
    font-weight: 800;
    line-height: 1.35;
    white-space: nowrap;
}

.capacity-current-value__marker {
    display: inline-flex;
    align-items: center;
    margin-left: 2px;
    color: #607087;
    cursor: help;
    font-size: 12px;
    font-weight: 850;
    line-height: 1;
}

.capacity-current-value__marker:focus {
    outline: 2px solid #8fb8e8;
    outline-offset: 2px;
}

.capacity-inline-edit {
    display: inline-flex;
    gap: 10px;
    align-items: center;
}

.capacity-mode-action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    min-height: 32px;
    padding: 0 12px;
    border: 1px solid #dbe4f0;
    border-radius: 8px;
    background: #f7fbff;
    color: #344055;
    font-size: 12px;
    font-weight: 800;
    line-height: 1;
    white-space: nowrap;
}

.capacity-mode-action--icon {
    width: 32px;
    min-width: 32px;
    padding: 0;
    border-color: transparent;
    background: transparent;
    color: #7a869a;
}

.capacity-mode-action:hover,
.capacity-mode-action:focus {
    border-color: #30a4b7;
    background: #30a4b7;
    color: #fff;
    text-decoration: none;
}

.capacity-mode-action--icon:hover,
.capacity-mode-action--icon:focus {
    border-color: #dbe4f0;
    background: #f7fbff;
    color: #344055;
}

.capacity-mode-action:focus {
    outline: 2px solid #8fb8e8;
    outline-offset: 2px;
}

.capacity-setting-input {
    width: 96px;
    flex: 0 0 96px;
}

.capacity-setting-input--personalized {
    min-height: 36px;
    border-color: #30a4b7;
    background: #fbfeff;
    color: #17233c;
    font-weight: 800;
}

.capacity-setting-input--personalized:focus {
    border-color: #2a8f9f;
    box-shadow: 0 0 0 3px rgba(48, 164, 183, 0.16);
}

.capacity-setting-unit {
    color: #6d7890;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.45;
}

.capacity-default-value {
    display: inline-flex;
    align-items: center;
    min-height: 34px;
    padding: 0 10px;
    border: 1px solid #e6edf6;
    border-radius: 8px;
    background: #f7fbff;
    color: #4b5871;
    font-size: 12px;
    font-weight: 800;
    line-height: 1.4;
    white-space: nowrap;
}

.capacity-defaults-panel {
    display: grid;
    grid-template-columns: minmax(180px, 1fr) auto auto;
    gap: 14px;
    align-items: flex-end;
    padding: 14px;
    border: 1px solid #e6edf6;
    border-radius: 10px;
    background: #f7fbff;
}

.capacity-defaults-panel__copy {
    display: grid;
    gap: 4px;
    min-width: 0;
}

.capacity-defaults-panel__copy strong {
    color: #344055;
    font-size: 13px;
    font-weight: 850;
    line-height: 1.25;
}

.capacity-defaults-panel__copy small {
    color: #6d7890;
    font-size: 11px;
    font-weight: 700;
    line-height: 1.45;
}

.capacity-default-field {
    display: grid;
    gap: 5px;
    margin: 0;
    color: #4b5871;
    font-size: 11px;
    font-weight: 800;
    line-height: 1.35;
}

.capacity-default-field span {
    white-space: nowrap;
}

.capacity-bulk-field {
    display: grid;
    gap: 5px;
    margin: 0;
    color: #4b5871;
    font-size: 11px;
    font-weight: 800;
    line-height: 1.35;
}

.capacity-bulk-field span {
    white-space: nowrap;
}

.capacity-slot-time {
    color: #344055;
    font-size: 12px;
    font-weight: 800;
}

.capacity-slot-panel {
    display: grid;
    gap: 14px;
    margin-top: 2px;
    padding: 12px 0 6px;
    border-bottom: 1px solid #edf2f8;
}

.capacity-slot-panel__hint {
    margin: 0;
    color: #6d7890;
    font-size: 11px;
    font-weight: 700;
    line-height: 1.45;
}

.capacity-selection-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 8px 10px;
    border: 1px solid #dbe4f0;
    border-radius: 8px;
    background: #f7fbff;
    color: #4b5871;
    font-size: 12px;
    font-weight: 800;
}

.capacity-selection-toolbar__button {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-height: 32px;
    padding: 0 12px;
    border: 1px solid #30a4b7;
    border-radius: 8px;
    background: #30a4b7;
    color: #fff;
    font-size: 12px;
    font-weight: 800;
    line-height: 1;
}

.capacity-selection-toolbar__button:hover,
.capacity-selection-toolbar__button:focus {
    background: #2a8f9f;
    border-color: #2a8f9f;
    color: #fff;
}

.capacity-modal-backdrop {
    position: fixed;
    inset: 0;
    z-index: 1050;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    background: rgba(28, 38, 56, 0.45);
}

.capacity-modal {
    display: grid;
    gap: 18px;
    width: min(560px, 100%);
    padding: 20px;
    border: 1px solid #dbe4f0;
    border-radius: 10px;
    background: #fff;
    box-shadow: 0 18px 48px rgba(31, 45, 61, 0.22);
}

.capacity-modal__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 14px;
}

.capacity-modal__header h5 {
    margin: 0;
    color: #344055;
    font-size: 17px;
    font-weight: 850;
    line-height: 1.3;
}

.capacity-modal__header p {
    margin: 5px 0 0;
    color: #607087;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.4;
}

.capacity-modal__close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border: 1px solid #dbe4f0;
    border-radius: 8px;
    background: #f7fbff;
    color: #344055;
}

.capacity-modal__body {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px;
}

.capacity-modal__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}

.capacity-modal__actions {
    display: inline-flex;
    align-items: center;
    gap: 10px;
}

.capacity-modal__secondary {
    padding-left: 0;
    color: #607087;
    font-size: 12px;
    font-weight: 800;
}

.capacity-modal__primary {
    min-height: 34px;
    padding: 0 14px;
    border: 1px solid #30a4b7;
    border-radius: 8px;
    background: #30a4b7;
    color: #fff;
    font-size: 12px;
    font-weight: 850;
}

.capacity-modal__primary:hover,
.capacity-modal__primary:focus {
    background: #2a8f9f;
    border-color: #2a8f9f;
}

.capacity-modal__primary:disabled {
    border-color: #b8c7da;
    background: #dbe4f0;
    color: #7a869a;
    cursor: not-allowed;
}

.capacity-slot-table-wrapper {
    overflow-x: auto;
    padding-bottom: 2px;
}

.capacity-slot-table {
    display: grid;
    min-width: 720px;
    overflow: hidden;
    border: 1px solid #e6edf6;
    border-radius: 10px;
    background: #fff;
}

.capacity-slot-table__header,
.capacity-slot-table__row {
    display: grid;
    grid-template-columns: 42px 88px minmax(220px, 1fr) minmax(220px, 1fr);
    gap: 10px;
    align-items: center;
    min-width: 0;
}

.capacity-slot-table__header {
    padding: 8px 10px;
    border-bottom: 1px solid #e6edf6;
    background: #f7fbff;
    color: #607087;
    font-size: 11px;
    font-weight: 800;
    text-transform: uppercase;
}

.capacity-slot-table__row {
    padding: 8px 10px;
    border-bottom: 1px solid #f0f4f9;
}

.capacity-slot-table__row:last-child {
    border-bottom: 0;
}

.capacity-slot-table__row .capacity-setting-input {
    width: 100%;
    max-width: 104px;
    min-height: 34px;
}

.capacity-slot-limit {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
    min-width: 0;
}

.capacity-slot-value {
    display: inline-flex;
    align-items: center;
    min-height: 30px;
    min-width: 48px;
    color: #344055;
    font-size: 12px;
    font-weight: 850;
    line-height: 1.25;
}

.capacity-slot-selection {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin: 0;
}

.capacity-slot-selection input {
    width: 16px;
    height: 16px;
    margin: 0;
}

.capacity-status-badge {
    display: inline-flex;
    align-items: center;
    min-height: 24px;
    padding: 2px 8px;
    border: 1px solid #dbe4f0;
    border-radius: 999px;
    background: #f7fbff;
    color: #607087;
    font-size: 11px;
    font-weight: 800;
    line-height: 1.2;
    white-space: nowrap;
}

.capacity-status-badge.is-custom {
    border-color: #b8c7da;
    background: #eef4fb;
    color: #344055;
}

.capacity-reset-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border: 1px solid transparent;
    border-radius: 8px;
    background: transparent;
    color: #607087;
}

.capacity-reset-button:hover,
.capacity-reset-button:focus {
    border-color: #dbe4f0;
    background: #f7fbff;
    color: #344055;
}

.capacity-reset-button:focus,
.capacity-slot-selection input:focus,
.capacity-setting-input:focus {
    outline: 2px solid #8fb8e8;
    outline-offset: 2px;
}

.capacity-empty-state {
    margin: 0;
    padding: 12px 14px;
    border: 1px dashed #dbe4f0;
    border-radius: 10px;
    color: #607087;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.45;
}

.rules-settings-list {
    display: grid;
    gap: 12px;
}

.rules-setting-row {
    display: grid;
    grid-template-columns: minmax(220px, 0.48fr) minmax(0, 1fr);
    gap: 18px;
    align-items: start;
    padding: 12px 0;
    border-bottom: 1px solid #edf2f8;
}

.rules-setting-row:last-child {
    border-bottom: 0;
}

.rules-setting-row--compact {
    align-items: center;
    padding: 8px 0;
}

.rules-setting-copy label {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-bottom: 0;
    color: #344055;
    font-weight: 700;
    line-height: 1.45;
}

.rules-setting-copy--inline label {
    margin-bottom: 0;
}

.rules-setting-copy small {
    display: block;
    margin-top: 5px;
    color: #7a869a;
    font-size: 12px;
    font-weight: 600;
    line-height: 1.45;
}

.rules-setting-control {
    display: flex;
    align-items: center;
    min-width: 0;
}

.rules-setting-control--stacked {
    display: grid;
    gap: 12px;
}

.rules-radio-group {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px 16px;
}

.rules-radio-choice {
    width: auto;
    min-height: 22px;
    margin: 0;
    color: #4b5871;
    font-size: 12px;
    font-weight: 800;
    line-height: 1.35;
}

.rules-radio-choice:focus-within .checkmark {
    outline: 2px solid #8fb8e8;
    outline-offset: 2px;
}

.rules-inline-fields {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    align-items: flex-end;
}

.rules-number-field {
    display: grid;
    gap: 5px;
    margin: 0;
    color: #4b5871;
    font-size: 11px;
    font-weight: 800;
    line-height: 1.35;
}

.rules-number-field > small {
    color: #7a869a;
    font-size: 11px;
    font-weight: 600;
    line-height: 1.35;
}

.rules-number-field--wide {
    grid-column: 1 / -1;
    max-width: 420px;
}

.rules-number-field__control {
    display: grid;
    grid-template-columns: 96px 132px;
    gap: 8px;
    align-items: center;
}

.rules-number-field__control > .form-control,
.rules-number-field__control > .form-select {
    height: calc(1.6em + 0.75rem + 2px);
    margin: 0;
}

.rules-number-field__control--single {
    grid-template-columns: 96px max-content;
}

.autoconfirm-fields {
    display: grid;
    grid-template-columns: repeat(2, minmax(160px, 1fr));
    gap: 12px;
    padding: 12px;
    border: 1px solid #e6edf6;
    border-radius: 10px;
    background: #f7fbff;
}

.average-table-time {
    display: grid;
    gap: 12px;
    margin: 10px 0 18px;
    padding: 12px 14px;
    border: 1px solid #e6edf6;
    border-radius: 12px;
    background: #fbfdff;
}

.average-table-time__title {
    color: #344055;
    font-size: 13px;
    font-weight: 800;
}

.average-table-time__default {
    display: inline-grid;
    grid-template-columns: max-content 112px;
    gap: 12px;
    align-items: center;
    justify-content: flex-start;
}

.average-table-time__default label {
    margin: 0;
    color: #4b5871;
    font-size: 12px;
    font-weight: 700;
}

.average-table-time__customize {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: #64748b;
    font-size: 12px;
    font-weight: 700;
}

.average-table-time__customize label {
    margin-bottom: 0;
}

.average-table-time__table {
    display: grid;
    overflow: hidden;
    max-width: 360px;
    border: 1px solid #e6edf6;
    border-radius: 10px;
    background: #fff;
}

.average-table-time__header,
.average-table-time__row {
    display: grid;
    grid-template-columns: minmax(110px, 1fr) 120px;
    gap: 12px;
    align-items: center;
}

.average-table-time__header {
    padding: 8px 10px;
    border-bottom: 1px solid #e6edf6;
    background: #f7fbff;
    color: #607087;
    font-size: 11px;
    font-weight: 800;
    text-transform: uppercase;
}

.average-table-time__row {
    padding: 6px 10px;
    border-bottom: 1px solid #f0f4f9;
    color: #344055;
    font-size: 12px;
    font-weight: 700;
}

.average-table-time__row:last-child {
    border-bottom: 0;
}

.restaurant-card-section__content {
    display: grid;
    grid-template-columns: minmax(180px, 0.35fr) minmax(0, 1fr);
    gap: 18px;
    align-items: flex-start;
}

.restaurant-card-section__label label {
    color: #344055;
    font-weight: 700;
}

.restaurant-card-section__choices {
    display: inline-flex;
    max-width: 100%;
    padding: 4px;
    border: 1px solid #dbe4f0;
    border-radius: 10px;
    background: #f7fbff;
    gap: 4px;
}

.restaurant-card-choice {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: auto;
    min-height: 34px;
    margin: 0;
    padding: 6px 12px;
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: #607087;
    font-size: 12px;
    font-weight: 800;
    line-height: 1.25;
    cursor: pointer;
}

.restaurant-card-choice input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
}

.restaurant-card-choice.is-selected {
    background: #fff;
    color: #247892;
    box-shadow: inset 0 0 0 1px rgba(48, 164, 183, 0.18);
}

.restaurant-card-choice:focus-within {
    outline: 2px solid #8fb8e8;
    outline-offset: 2px;
}

.restaurant-card-section__field {
    margin-top: 14px;
}

@media (max-width: 767.98px) {
    .capacity-setting-row {
        grid-template-columns: 1fr;
    }

    .capacity-setting-input {
        width: 100%;
        max-width: 160px;
        flex-basis: 160px;
    }

    .restaurant-card-section__content {
        grid-template-columns: 1fr;
    }

    .restaurant-card-section__choices {
        display: grid;
        grid-template-columns: 1fr;
        width: 100%;
    }

    .capacity-setting-control {
        grid-template-columns: 1fr;
    }

    .capacity-current-value {
        white-space: normal;
    }

    .capacity-selection-toolbar {
        align-items: flex-start;
        flex-direction: column;
    }

    .capacity-defaults-panel {
        grid-template-columns: 1fr;
    }

    .capacity-default-field .capacity-setting-input {
        width: 100%;
    }

    .capacity-selection-toolbar__button {
        width: 100%;
        justify-content: center;
    }

    .capacity-modal {
        max-height: calc(100vh - 40px);
        overflow-y: auto;
    }

    .capacity-modal__body,
    .capacity-modal__footer,
    .capacity-modal__actions {
        display: grid;
        grid-template-columns: 1fr;
        width: 100%;
    }

    .capacity-slot-table__row .capacity-setting-input {
        max-width: none;
    }

    .capacity-slot-limit {
        flex-wrap: nowrap;
    }

    .rules-setting-row {
        grid-template-columns: 1fr;
    }

    .autoconfirm-fields,
    .rules-number-field__control,
    .rules-number-field__control--single {
        grid-template-columns: 1fr;
    }

    .rules-radio-group {
        display: grid;
        gap: 10px;
    }

    .rules-radio-choice {
        width: auto;
    }

    .average-table-time__default,
    .average-table-time__header,
    .average-table-time__row {
        grid-template-columns: 1fr;
    }

    .average-table-time__table {
        max-width: none;
    }
}
</style>
